import { UTCDate } from '@date-fns/utc';
import {
  addHours,
  addMinutes,
  compareAsc,
  compareDesc,
  differenceInDays,
  endOfMonth,
  format,
  getTime,
  isValid,
  isWeekend,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
} from 'date-fns';

/**
 * Type for date input values
 * - string: ISO date string (e.g., '2026-01-21', '2026-01-21T14:30:00')
 * - number: timestamp in milliseconds (e.g., Date.now(), date.getTime())
 * - Date: JavaScript Date object
 */
export type DateInput = string | number | Date;

/**
 * Type for date input values that can be nullable
 * Used in formatting functions that return empty string for null/undefined
 */
export type DateInputNullable = DateInput | null | undefined;

/**
 * Converts a given value to a UTC-based Date object.
 *
 * - If the date is in "YYYY-MM-DD" format (without time), it is treated as UTC midnight.
 * - Otherwise, applies the old logic using timezone offsets.
 *
 * @param {string|number|Date} value - The date input. Can be a timestamp, date object, or "YYYY-MM-DD" string.
 * @param {boolean} negative - If true, adjusts time using negative timezone offset.
 * @returns {Date} A Date object representing the UTC time.
 */
export const toUTCDate = (value: DateInput, negative?: boolean): Date => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // If value is in "YYYY-MM-DD" format, ensure UTC without timezone shift
    const [year, month, day] = value.split('-').map(Number);
    return new Date(Date.UTC(year!, month! - 1, day!));
  }

  // Otherwise, use the old logic with timezone offsets
  const timeZoneOffset = negative ? -new Date(value).getTimezoneOffset() : new Date(value).getTimezoneOffset();

  return addMinutes(new Date(value), timeZoneOffset);
};

export const toUTCDateISOString = (value: DateInput, negative?: boolean): string => {
  return toUTCDate(value, negative).toISOString();
};

export const toIsoDateTimeWithOffset = (date: Date): string =>
  `${format(date, DATE_FORMATS.DATE_TIME_OFFSET)}${format(date, 'xxx')}`;

/**
 * Converts a local "start of day" timestamp to UTC midnight ISO string
 * for the same calendar date (ignores the local time part).
 *
 * For use during migration from local to full UTC handling.
 *
 * Example:
 *   Local: 2025-03-29T00:00:00 → UTC: "2025-03-29T00:00:00.000Z"
 *   Local: 2025-03-29T10:00:00 → UTC: "2025-03-29T00:00:00.000Z"
 */
export const localMidnightToUTCMidnightISOString = (value: DateInput): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  return new Date(Date.UTC(year, month, day)).toISOString();
};

/**
 * Converts a local "end of day" timestamp to UTC 23:59:59 ISO string
 * for the same calendar date (ignores the local time part).
 *
 * For use during migration from local to full UTC handling.
 *
 * Example:
 *   Local: 2025-04-04T23:59:59 → UTC: "2025-04-04T23:59:59.000Z"
 *   Local: 2025-04-04T12:00:00 → UTC: "2025-04-04T23:59:59.000Z"
 */
export const localEndOfDayToUTCEndOfDayISOString = (value: DateInput): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  return new Date(Date.UTC(year, month, day, 23, 59, 59, 0)).toISOString();
};

/**
 * Returns default name for a newly created campaign: "C" + sortable UTC timestamp.
 * Example: "C 2026-05-11 14:30:45"
 */
export const getDefaultNewCampaignName = (): string => {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  const datePart = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`;
  const timePart = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
  return `C ${datePart} ${timePart}`;
};

export const toEndDate = (date: Date): Date => {
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  return date;
};

export const getLastWeek = () => {
  const today = new Date();
  return {
    start: getTime(subDays(today.setHours(0, 0, 0, 0), 6)),
    end: getTime(today.setHours(23, 59, 59, 0)),
  };
};

export const getLastFullWeek = () => {
  const today = new Date();
  return {
    start: getTime(subDays(today.setHours(0, 0, 0, 0), 7)),
    end: getTime(subDays(today.setHours(23, 59, 59, 0), 1)),
  };
};

export const getDateRangeForTheLastXHoursInUTC = (hours: number) => {
  const today = new Date();
  return {
    start: subHours(today, hours),
    end: today,
  };
};

export const secondsToDhms = (sec: number | string): string => {
  const seconds = Number(sec);
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const dDisplay = d > 0 ? d + (d == 1 ? ' day ' : ' days, ') : '';
  const hDisplay = h > 0 ? h + (h == 1 ? ' hour ' : ' hours ') : '';
  const mDisplay = m > 0 ? m + (m == 1 ? ' minute ' : ' minutes ') : '';
  const sDisplay = s > 0 ? s + (s == 1 ? ' second' : ' seconds') : '';
  return dDisplay + hDisplay + mDisplay + sDisplay;
};

/**
 * Date format patterns used by formatter functions
 */
const DATE_FORMATS = {
  DATE: 'dd.MM.yyyy',
  DATE_TIME: 'dd.MM.yyyy HH:mm',
  DATE_TIME_SECONDS: 'dd.MM.yyyy HH:mm:ss',
  DATE_TIME_COMPACT: 'dd.MM.yy HH:mm',
  DATE_WITH_MONTH: 'dd MMM, yyyy',
  DATE_TIME_WITH_MONTH: 'dd MMM, yyyy HH:mm',
  DATE_ISO: 'yyyy-MM-dd',
  DATE_DASHED: 'dd-MM-yyyy',
  TIME: 'HH:mm',
  DATE_TIME_WEEKDAY: 'dd.MM.yyyy HH:mm, EEEE',
  DATE_TIME_OFFSET: "yyyy-MM-dd'T'HH:mm:ss.SSS",
} as const;

/**
 * Factory function to create date formatters with a given pattern
 */
const createDateFormatter = (formatPattern: string): ((value: DateInputNullable) => string) => {
  return (value: DateInputNullable): string => {
    if (!value) {
      return '';
    }
    return format(new Date(value), formatPattern);
  };
};

/**
 * Factory function to create formatters that render the UTC components of a date
 *
 * `UTCDate` is date-fns v4's own UTC time-zone extension: it overrides the local getters date-fns reads,
 * so no proxy Date is constructed and no local calendar (incl. DST transitions) can distort the output.
 */
const createUTCDateFormatter = (formatPattern: string): ((value: DateInputNullable) => string) => {
  return (value: DateInputNullable): string => {
    if (!value) {
      return '';
    }
    return format(new UTCDate(value), formatPattern);
  };
};

export const parseIsoDateTime = (value: string | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

/**
 * Formats a date to 'DD.MM.YYYY' format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date string or empty string if value is falsy
 *
 * @example
 * formatDate(new Date('2026-01-21')) // '21.01.2026'
 * formatDate('2026-01-21T14:30:00') // '21.01.2026'
 * formatDate(1737468600000) // '21.01.2026'
 * formatDate(null) // ''
 */
export const formatDate = createDateFormatter(DATE_FORMATS.DATE);

/**
 * Formats a date to 'DD.MM.YYYY HH:mm' format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date-time string or empty string if value is falsy
 *
 * @example
 * formatDateTime(new Date('2026-01-21T14:30:00')) // '21.01.2026 14:30'
 * formatDateTime('2026-01-21T14:30:00') // '21.01.2026 14:30'
 * formatDateTime(1737468600000) // '21.01.2026 14:30'
 * formatDateTime(null) // ''
 */
export const formatDateTime = createDateFormatter(DATE_FORMATS.DATE_TIME);

/**
 * Formats a date to 'DD.MM.YYYY HH:mm:ss' format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date-time string with seconds or empty string if value is falsy
 *
 * @example
 * formatDateTimeWithSeconds(new Date('2026-01-21T14:30:45')) // '21.01.2026 14:30:45'
 * formatDateTimeWithSeconds('2026-01-21T14:30:45') // '21.01.2026 14:30:45'
 * formatDateTimeWithSeconds(1737468645000) // '21.01.2026 14:30:45'
 * formatDateTimeWithSeconds(null) // ''
 */
export const formatDateTimeWithSeconds = createDateFormatter(DATE_FORMATS.DATE_TIME_SECONDS);

/**
 * Formats a date to 'DD.MM.YY HH:mm' compact format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date-time string in compact format or empty string if value is falsy
 *
 * @example
 * formatDateTimeCompact(new Date('2026-01-21T14:30:00')) // '21.01.26 14:30'
 * formatDateTimeCompact('2026-01-21T14:30:00') // '21.01.26 14:30'
 * formatDateTimeCompact(1737468600000) // '21.01.26 14:30'
 * formatDateTimeCompact(null) // ''
 */
export const formatDateTimeCompact = createDateFormatter(DATE_FORMATS.DATE_TIME_COMPACT);

/**
 * Formats a date to 'DD MMM, YYYY' format with abbreviated month name.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date string with month name or empty string if value is falsy
 *
 * @example
 * formatDateWithMonth(new Date('2026-01-21')) // '21 Jan, 2026'
 * formatDateWithMonth('2026-01-21') // '21 Jan, 2026'
 * formatDateWithMonth(1737468600000) // '21 Jan, 2026'
 * formatDateWithMonth(null) // ''
 */
export const formatDateWithMonth = createDateFormatter(DATE_FORMATS.DATE_WITH_MONTH);

/**
 * Formats a date to 'DD MMM, YYYY HH:mm' format with abbreviated month name.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date-time string with month name or empty string if value is falsy
 *
 * @example
 * formatDateTimeWithMonth(new Date('2026-01-21T14:30:00')) // '21 Jan, 2026 14:30'
 * formatDateTimeWithMonth('2026-01-21T14:30:00') // '21 Jan, 2026 14:30'
 * formatDateTimeWithMonth(1737468600000) // '21 Jan, 2026 14:30'
 * formatDateTimeWithMonth(null) // ''
 */
export const formatDateTimeWithMonth = createDateFormatter(DATE_FORMATS.DATE_TIME_WITH_MONTH);

/**
 * Formats a date to 'YYYY-MM-DD' ISO format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date string in ISO format or empty string if value is falsy
 *
 * @example
 * formatDateIso(new Date('2026-01-21')) // '2026-01-21'
 * formatDateIso('2026-01-21T14:30:00') // '2026-01-21'
 * formatDateIso(1737468600000) // '2026-01-21'
 * formatDateIso(null) // ''
 */
export const formatDateIso = createDateFormatter(DATE_FORMATS.DATE_ISO);

/**
 * Formats a date to 'DD-MM-YYYY' dashed format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date string with dashes or empty string if value is falsy
 *
 * @example
 * formatDateDashed(new Date('2026-01-21')) // '21-01-2026'
 * formatDateDashed('2026-01-21T14:30:00') // '21-01-2026'
 * formatDateDashed(1737468600000) // '21-01-2026'
 * formatDateDashed(null) // ''
 */
export const formatDateDashed = createDateFormatter(DATE_FORMATS.DATE_DASHED);

/**
 * Formats a date to 'HH:mm' time-only format.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted time string or empty string if value is falsy
 *
 * @example
 * formatTime(new Date('2026-01-21T14:30:00')) // '14:30'
 * formatTime('2026-01-21T14:30:00') // '14:30'
 * formatTime(1737468600000) // '14:30'
 * formatTime(null) // ''
 */
export const formatTime = createDateFormatter(DATE_FORMATS.TIME);

/**
 * Formats a date to 'DD.MM.YYYY HH:mm, dddd' format with weekday name.
 *
 * @param {DateInput} value - The date to format (Date, string, or timestamp)
 * @returns {string} Formatted date-time string with weekday or empty string if value is falsy
 *
 * @example
 * formatDateTimeWithWeekday(new Date('2026-01-21T14:30:00')) // '21.01.2026 14:30, Wednesday'
 * formatDateTimeWithWeekday('2026-01-21T14:30:00') // '21.01.2026 14:30, Wednesday'
 * formatDateTimeWithWeekday(1737468600000) // '21.01.2026 14:30, Wednesday'
 * formatDateTimeWithWeekday(null) // ''
 */
export const formatDateTimeWithWeekday = createDateFormatter(DATE_FORMATS.DATE_TIME_WEEKDAY);

/**
 * UTC counterpart of {@link formatDate} — renders 'DD.MM.YYYY' in UTC, fully independent of the browser timezone and its DST rules.
 *
 * @example
 * formatDateUTC('2026-01-21T23:30:00Z') // '21.01.2026' (even in UTC+3)
 */
export const formatDateUTC = createUTCDateFormatter(DATE_FORMATS.DATE);

/**
 * UTC counterpart of {@link formatDateTime} — renders 'DD.MM.YYYY HH:mm' in UTC, fully independent of the browser timezone and its DST rules.
 *
 * @example
 * formatDateTimeUTC('2026-01-21T23:30:00Z') // '21.01.2026 23:30' (even in UTC+3)
 */
export const formatDateTimeUTC = createUTCDateFormatter(DATE_FORMATS.DATE_TIME);

/**
 * UTC counterpart of {@link formatTime} — renders 'HH:mm' in UTC, fully independent of the browser timezone and its DST rules.
 *
 * @example
 * formatTimeUTC('2026-01-21T23:30:00Z') // '23:30' (even in UTC+3)
 */
export const formatTimeUTC = createUTCDateFormatter(DATE_FORMATS.TIME);

/**
 * UTC counterpart of {@link formatDateTimeWithMonth} — renders 'DD MMM, YYYY HH:mm' in UTC,
 * fully independent of the browser timezone and its DST rules.
 *
 * @example
 * formatDateTimeWithMonthUTC('2026-01-21T23:30:00Z') // '21 Jan, 2026 23:30' (even in UTC+3)
 */
export const formatDateTimeWithMonthUTC = createUTCDateFormatter(DATE_FORMATS.DATE_TIME_WITH_MONTH);

// Re-export date-fns functions used across the app
export {
  addHours,
  addMinutes,
  compareAsc,
  compareDesc,
  differenceInDays,
  endOfMonth,
  getTime,
  isWeekend,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
};
